import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { addMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from '../i18n';
import type { Locale } from '../i18n';
import { buildMonthGrid, groupDealsByDay, isOverdue, undatedCount } from '../lib/calendar';
import type { Inquiry } from '../types/api';
import styles from './InquiriesCalendar.module.css';

export interface InquiriesCalendarProps {
  items: Inquiry[];
  onOpen: (inquiry: Inquiry) => void;
}

const intlLocale = (locale: Locale) => (locale === 'ru' ? 'ru-RU' : 'en-US');

/**
 * Issue #53 — month calendar of deals by next-step date (Bitrix-style).
 * Same data source as the «По делам» board: open deals only, grouped by
 * `nextStepAt`. Overdue deals keep the red tint of the activity board.
 * Month / weekday names come from `Intl.DateTimeFormat` — never hardcoded.
 */
export function InquiriesCalendar({ items, onOpen }: InquiriesCalendarProps) {
  const { t, locale } = useT();
  const [anchor, setAnchor] = useState(() => new Date());

  const weeks = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const byDay = useMemo(() => groupDealsByDay(items), [items]);
  const undated = useMemo(() => undatedCount(items), [items]);

  const tag = intlLocale(locale);
  const monthTitle = new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric' }).format(anchor);
  // Monday-first weekday header, matching the grid.
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(tag, { weekday: 'short' });
    // 2024-01-01 was a Monday — a stable anchor for any locale.
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [tag]);

  return (
    <section className={styles.wrap} aria-label={t('inquiries.viewCalendar')}>
      <header className={styles.bar}>
        <h2 className={styles.title}>{monthTitle}</h2>
        <div className={styles.nav}>
          <button
            type="button"
            className={styles.navBtn}
            aria-label={t('calendar.prevMonth')}
            onClick={() => setAnchor((d) => addMonths(d, -1))}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={styles.todayBtn}
            onClick={() => setAnchor(new Date())}
          >
            {t('calendar.today')}
          </button>
          <button
            type="button"
            className={styles.navBtn}
            aria-label={t('calendar.nextMonth')}
            onClick={() => setAnchor((d) => addMonths(d, 1))}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className={styles.grid} role="grid">
        <div className={styles.weekdays} role="row">
          {weekdays.map((d) => (
            <div key={d} className={styles.weekday} role="columnheader">
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week) => (
          <div key={week[0].key} className={styles.week} role="row">
            {week.map((day) => {
              const deals = byDay.get(day.key) ?? [];
              return (
                <div
                  key={day.key}
                  role="gridcell"
                  className={clsx(
                    styles.cell,
                    !day.inMonth && styles.cellOutside,
                    day.isToday && styles.cellToday,
                  )}
                >
                  <span className={clsx(styles.dayNum, day.isToday && styles.dayNumToday)}>
                    {day.date.getDate()}
                  </span>
                  <div className={styles.deals}>
                    {deals.map((inq) => {
                      const overdue = isOverdue(inq);
                      const name = inq.client?.companyName ?? t('common.noClient');
                      return (
                        <button
                          key={inq.id}
                          type="button"
                          className={clsx(styles.deal, overdue && styles.dealOverdue)}
                          title={`${name} — ${inq.summary}`}
                          onClick={() => onOpen(inq)}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {undated > 0 && (
        <footer className={styles.foot}>
          <span>{t('calendar.noDate', { count: undated })}</span>
        </footer>
      )}
    </section>
  );
}
